/**
 * Detail + edit modal for one auction vehicle. Opens on row click in
 * AuctionRunTable; the table stays the lean read-only view.
 *
 * Layout (V2 spec):
 *   - Header: "Run # · Lane · Year Make Model", then "Stock # · VIN".
 *     Top-right: a Store dropdown (rooftop the car counts toward) + close.
 *   - Disposition (live).
 *   - Reference: Age / Appraiser / Odometer + KBB Lending / Trade (read-only).
 *   - Cost panel: every cost editable (prefilled from VAUTO) with an
 *     include-in-total checkbox. Unit Cost is required and always counted;
 *     Extra Cost is shown as "Adjustments (±)" and may be negative. Total Cost
 *     sums only the checked rows, mirroring the backend.
 *   - Auction results: Sold, High Bid, Best When, Notes.
 *   - Apply saves all changed fields at once via editVehicle, then closes.
 *
 * Modal shell mirrors AuctionRowAlertModal. When the run is completed the
 * fields are read-only and Apply is hidden.
 */
import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

import { AuctionReconNote, AuctionVehicle, AuctionVehicleEditRequest } from "../../api/wholesaleAuction"
import { THEME } from "../../config/theme"
import { useWholesaleAuction } from "../../context/WholesaleAuctionContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { formatInt, formatMoney, parseMoney } from "../../utils/wholesaleAuctionFormat"

interface Props {
  vehicle: AuctionVehicle
  locked: boolean
  // Rooftop labels the car can be assigned to (the run's rooftop columns).
  rooftops: string[]
  onClose: () => void
}

type CostKey =
  | "unit_cost"
  | "reconditioning_cost"
  | "pack_cost"
  | "transportation_cost"
  | "other_cost"
  | "extra_cost"

type FlagKey =
  | "include_unit_cost"
  | "include_reconditioning_cost"
  | "include_pack_cost"
  | "include_transportation_cost"
  | "include_other_cost"
  | "include_extra_cost"

interface CostRowDef {
  key: CostKey
  flag: FlagKey
  label: string
  required?: boolean
}

// Cost rows in display order. Unit Cost is required + always included; the
// rest are opt-in via their checkbox. Total Cost = sum of the checked rows.
const COST_ROWS: CostRowDef[] = [
  { key: "unit_cost", flag: "include_unit_cost", label: "Unit Cost", required: true },
  { key: "reconditioning_cost", flag: "include_reconditioning_cost", label: "Recon" },
  { key: "pack_cost", flag: "include_pack_cost", label: "Pack" },
  { key: "transportation_cost", flag: "include_transportation_cost", label: "Trans" },
  { key: "other_cost", flag: "include_other_cost", label: "Other" },
  { key: "extra_cost", flag: "include_extra_cost", label: "Adjustments (±)" },
]

export default function VehicleDetailModal({ vehicle, locked, rooftops, onClose }: Props) {
  const { editVehicle } = useWholesaleAuction()

  const [costs, setCosts] = useState<Record<CostKey, string>>(() => initCosts(vehicle))
  const [flags, setFlags] = useState<Record<FlagKey, boolean>>(() => initFlags(vehicle))
  // Pre-select the rooftop the car actually routes to: the explicit override
  // when set, otherwise the rooftop the vAuto store resolves to (rooftop_label).
  // "" → the "Follow vAuto (auto)" entry (used when nothing resolves).
  const [storeOverride, setStoreOverride] = useState(
    vehicle.store_override ?? vehicle.rooftop_label ?? "",
  )
  const [sold, setSold] = useState(moneyToInput(vehicle.sold))
  const [highBid, setHighBid] = useState(moneyToInput(vehicle.high_bid))
  const [bestWhen, setBestWhen] = useState(vehicle.best_when ?? "")
  const [notes, setNotes] = useState(vehicle.notes ?? "")
  const [saving, setSaving] = useState(false)
  const isMobile = useIsMobile()
  const [error, setError] = useState<string | null>(null)

  // Esc-to-close — UX expectation for any modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  // Live Total Cost + Disposition mirror the backend math: only checked rows
  // count, and Unit Cost is always counted.
  const totalCost = computeTotal(costs, flags)
  const soldParsed = parseMoney(sold)
  const soldValue = soldParsed != null && !Number.isNaN(soldParsed) ? soldParsed : null
  const disposition = soldValue != null && totalCost != null ? soldValue - totalCost : null

  const handleApply = async () => {
    const unit = readMoney(costs.unit_cost)
    if (!unit.ok || unit.value == null) {
      setError("Unit Cost is required.")
      return
    }
    const parsedCosts: Partial<Record<CostKey, number | null>> = {}
    for (const row of COST_ROWS) {
      const r = readMoney(costs[row.key])
      if (!r.ok) {
        setError("Enter valid numbers for the cost fields.")
        return
      }
      parsedCosts[row.key] = r.value
    }
    const soldRead = readMoney(sold)
    const highBidRead = readMoney(highBid)
    if (!soldRead.ok || !highBidRead.ok) {
      setError("Enter valid numbers for Sold and High Bid.")
      return
    }

    const updates: AuctionVehicleEditRequest = {
      ...parsedCosts,
      include_unit_cost: true, // always counted
      include_reconditioning_cost: flags.include_reconditioning_cost,
      include_pack_cost: flags.include_pack_cost,
      include_transportation_cost: flags.include_transportation_cost,
      include_other_cost: flags.include_other_cost,
      include_extra_cost: flags.include_extra_cost,
      store_override: storeOverride || null,
      sold: soldRead.value,
      high_bid: highBidRead.value,
      best_when: bestWhen.trim() || null,
      notes: notes.trim() || null,
    }

    setSaving(true)
    setError(null)
    try {
      await editVehicle(vehicle.id, updates)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  const year = vehicle.year_vauto ?? vehicle.year_adesa
  const make = vehicle.make_vauto ?? vehicle.make_adesa
  const model = vehicle.model_vauto ?? vehicle.model_adesa
  const vehicleLabel = [year, make, model].filter(Boolean).join(" ") || "Vehicle"
  const titleParts = [
    vehicle.run_number != null ? `Run ${vehicle.run_number}` : null,
    vehicle.lane ? `Lane ${vehicle.lane}` : null,
    vehicleLabel,
  ].filter(Boolean)
  const title = titleParts.join("  ·  ")
  const subline = [
    vehicle.client_id ? `Stock ${vehicle.client_id}` : null,
    vehicle.vin ? `VIN ${vehicle.vin}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ")
  const odometer = vehicle.odometer_vauto ?? vehicle.odo_adesa

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={containerStyle}>
        {/* Header: title block + store dropdown (top-right) + close. On mobile
            the two stack so the title gets the full width. */}
        <div
          style={{
            ...headerRowStyle,
            ...(isMobile && { flexDirection: "column", gap: THEME.spacing.sm }),
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={titleStyle}>{title}</div>
            {subline && <div style={subtitleStyle}>{subline}</div>}
          </div>
          <div
            style={{
              ...headerRightStyle,
              ...(isMobile && { width: "100%", justifyContent: "space-between" }),
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={captionStyle}>Store</span>
              <select
                value={storeOverride}
                onChange={(e) => setStoreOverride(e.target.value)}
                disabled={locked || saving}
                style={selectStyle}
              >
                <option value="">Follow vAuto (auto)</option>
                {rooftops.map((label) => (
                  <option key={label} value={label}>{label}</option>
                ))}
              </select>
              <span style={{ ...subtitleStyle, marginTop: 0 }}>
                {vehicle.vauto_store ? `vAuto: ${vehicle.vauto_store}` : "No vAuto store"}
              </span>
            </label>
            <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close">
              <X size={18} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Disposition — headline number */}
        <div style={dispositionRowStyle}>
          <span style={captionStyle}>Disposition</span>
          <span style={dispositionValueStyle(disposition)}>{moneyOrDash(disposition)}</span>
        </div>

        {/* Auction results — above the reference boxes (you fill these in) */}
        <div style={actionPanelStyle}>
          <div style={captionStyle}>Auction Results</div>
          <div style={actionGridStyle}>
            <Field label="Sold">
              <input type="text" value={sold} onChange={(e) => setSold(e.target.value)}
                disabled={locked || saving} placeholder="$0" style={fieldInputStyle} />
            </Field>
            <Field label="High Bid">
              <input type="text" value={highBid} onChange={(e) => setHighBid(e.target.value)}
                disabled={locked || saving} placeholder="$0" style={fieldInputStyle} />
            </Field>
            <Field label="Best When">
              <input type="text" value={bestWhen} onChange={(e) => setBestWhen(e.target.value)}
                disabled={locked || saving} style={fieldInputStyle} />
            </Field>
          </div>
          <Field label="Auction Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              disabled={locked || saving} rows={3} style={textareaStyle} />
          </Field>
        </div>

        {/* Three boxes side by side: Details · Market Value · Cost */}
        <div style={panelsRowStyle}>
          <Panel title="Details">
            <Stat label="Age" value={vehicle.age != null ? `${vehicle.age} days` : "—"} />
            <Stat label="Appraiser" value={textOrDash(vehicle.appraiser)} />
            <Stat label="Odometer" value={odometer != null ? formatInt(odometer) : "—"} />
          </Panel>
          <Panel title="Market Value">
            <Stat label="KBB Lending" value={moneyOrDash(vehicle.kbb_lending)} />
            <Stat label="KBB Trade-In" value={moneyOrDash(vehicle.kbb_trade_in)} />
          </Panel>
          {/* Cost — each cost editable + include-in-total checkbox */}
          <div style={costPanelStyle}>
            <div style={{ ...captionStyle, marginBottom: THEME.spacing.sm }}>
              Cost <span style={costHintStyle}>(check = in total)</span>
            </div>
            {COST_ROWS.map((row) => (
              <CostRow
                key={row.key}
                label={row.label}
                required={!!row.required}
                value={costs[row.key]}
                onValue={(s) => setCosts((c) => ({ ...c, [row.key]: s }))}
                included={!!row.required || flags[row.flag]}
                onToggle={(b) => setFlags((f) => ({ ...f, [row.flag]: b }))}
                disabled={locked || saving}
              />
            ))}
            <div style={totalRowStyle}>
              <span style={{ fontWeight: 600 }}>Total Cost</span>
              <span style={{ fontWeight: 600 }}>{moneyOrDash(totalCost)}</span>
            </div>
          </div>
        </div>

        {/* Vehicle Description — read-only VAUTO comments, above the recon notes. */}
        <VehicleDescriptionPanel vehicle={vehicle} />

        {/* Rapid Recon notes — read-only, sourced from the recon match. */}
        <RapidReconNotesPanel vehicle={vehicle} />

        {/* Footer */}
        <div style={footerStyle}>
          {error && <span style={errorTextStyle}>{error}</span>}
          {locked && (
            <span style={{ ...subtitleStyle, fontStyle: "italic" }}>
              Run completed — read-only. Reopen the run to edit.
            </span>
          )}
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            {locked ? "Close" : "Cancel"}
          </button>
          {!locked && (
            <button type="button" onClick={handleApply} disabled={saving} style={primaryButtonStyle(!saving)}>
              {saving ? "Applying…" : "Apply"}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={panelStyle}>
      <div style={{ ...captionStyle, marginBottom: THEME.spacing.sm }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>
    </div>
  )
}

// Read-only VAUTO "Vehicle Description" comments, shown directly above the
// Rapid Recon notes. Sourced straight from the VAUTO export, so it follows the
// VAUTO-resolved identity: empty until the row is matched or a candidate picked.
// Reuses the recon panel's styles — it's the sibling read-only follow-the-match
// panel.
function VehicleDescriptionPanel({ vehicle }: { vehicle: AuctionVehicle }) {
  const description = (vehicle.vehicle_description ?? "").trim()
  const resolved =
    vehicle.match_state === "matched" || vehicle.match_state === "user_selected"

  let body: React.ReactNode
  if (description) {
    body = <div style={rrNotesTextStyle}>{description}</div>
  } else if (resolved) {
    body = <span style={rrEmptyStyle}>Matched — no vAuto notes recorded.</span>
  } else if (vehicle.match_state === "ambiguous") {
    body = (
      <span style={rrEmptyStyle}>
        vAuto notes can't be populated until the vAuto match is resolved — click the
        row's status to pick a record.
      </span>
    )
  } else if (vehicle.match_state === "no_match") {
    body = (
      <span style={rrEmptyStyle}>
        vAuto notes can't be populated until the vAuto match is resolved.
      </span>
    )
  } else {
    body = <span style={rrEmptyStyle}>No vAuto notes recorded.</span>
  }

  return (
    <div style={rrPanelStyle}>
      <div style={{ ...captionStyle, marginBottom: THEME.spacing.sm }}>vAuto Notes</div>
      {body}
    </div>
  )
}

// Collapse recon matches to DISTINCT notes (trimmed, non-empty), first source
// wins per note text. The DOLANRECON catch-all mirrors the per-rooftop files, so
// the same note routinely shows up twice — this keeps it from rendering twice.
function dedupeReconNotes(
  matches: AuctionReconNote[] | null,
): { source: string | null; notes: string }[] {
  const distinct: { source: string | null; notes: string }[] = []
  for (const match of matches ?? []) {
    const notes = (match.notes ?? "").trim()
    if (notes && !distinct.some((d) => d.notes === notes)) {
      distinct.push({ source: match.source, notes })
    }
  }
  return distinct
}

// Read-only recon log, shown below the Details / Market Value / Cost boxes.
// Recon FOLLOWS the VAUTO match, so the notes describe the VAUTO-resolved car:
// rr_match_state is "matched" / "no_match", or null while the VAUTO match is
// still ambiguous (notes load once the VAUTO pick is made).
function RapidReconNotesPanel({ vehicle }: { vehicle: AuctionVehicle }) {
  const state = vehicle.rr_match_state
  // A car can carry notes from several recon files, but the DOLANRECON catch-all
  // mirrors the per-rooftop files, so the same note usually appears twice. Dedupe
  // by note text and show each DISTINCT note once. Only tack on a source label
  // when notes genuinely differ — a single deduped note is just "Rapid Recon Notes".
  const distinct = dedupeReconNotes(vehicle.rr_matches)
  const vautoUnresolved = vehicle.match_state === "ambiguous"
  const vautoNoMatch = vehicle.match_state === "no_match"

  if (state === "matched" && distinct.length > 0) {
    const showSource = distinct.length > 1
    return (
      <>
        {distinct.map((d, i) => (
          <div key={i} style={rrPanelStyle}>
            <div style={{ ...captionStyle, marginBottom: THEME.spacing.sm }}>
              {showSource && d.source
                ? `Rapid Recon Notes · ${d.source}`
                : "Rapid Recon Notes"}
            </div>
            <div style={rrNotesTextStyle}>{d.notes}</div>
          </div>
        ))}
      </>
    )
  }

  let body: React.ReactNode
  if (state === "matched") {
    body = <span style={rrEmptyStyle}>Matched — no recon notes recorded.</span>
  } else if (vautoUnresolved) {
    body = (
      <span style={rrEmptyStyle}>
        Rapid Recon notes can't be populated until the vAuto match is resolved — click the
        row's status to pick a record.
      </span>
    )
  } else if (vautoNoMatch) {
    body = (
      <span style={rrEmptyStyle}>
        Rapid Recon notes can't be populated until the vAuto match is resolved.
      </span>
    )
  } else {
    body = <span style={rrEmptyStyle}>No Rapid Recon record matched this vehicle.</span>
  }

  return (
    <div style={rrPanelStyle}>
      <div style={{ ...captionStyle, marginBottom: THEME.spacing.sm }}>Rapid Recon Notes</div>
      {body}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={statRowStyle}>
      <span style={statLabelStyle}>{label}</span>
      <span style={statValueStyle}>{value}</span>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={statLabelStyle}>{label}</span>
      {children}
    </label>
  )
}

function CostRow({
  label,
  required,
  value,
  onValue,
  included,
  onToggle,
  disabled,
}: {
  label: string
  required: boolean
  value: string
  onValue: (s: string) => void
  included: boolean
  onToggle: (b: boolean) => void
  disabled: boolean
}) {
  return (
    <div style={costRowStyle}>
      <input
        type="checkbox"
        checked={included}
        // Unit Cost is always counted — its checkbox is locked on.
        disabled={disabled || required}
        onChange={(e) => onToggle(e.target.checked)}
        title={required ? "Always included in Total" : "Include in Total Cost"}
        style={{ accentColor: THEME.colors.navy, cursor: required ? "default" : "pointer" }}
      />
      <span style={statLabelStyle}>
        {label}
        {required && " *"}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onValue(e.target.value)}
        disabled={disabled}
        placeholder="$0"
        style={inlineInputStyle}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Parse / format helpers
// ---------------------------------------------------------------------------

function initCosts(vehicle: AuctionVehicle): Record<CostKey, string> {
  const out = {} as Record<CostKey, string>
  for (const row of COST_ROWS) out[row.key] = moneyToInput(vehicle[row.key])
  return out
}

function initFlags(vehicle: AuctionVehicle): Record<FlagKey, boolean> {
  const out = {} as Record<FlagKey, boolean>
  for (const row of COST_ROWS) out[row.flag] = vehicle[row.flag]
  return out
}

// Total = sum of the included rows (Unit Cost always counts), or null when no
// included row has a value. Mirrors backend compute.total_cost.
function computeTotal(costs: Record<CostKey, string>, flags: Record<FlagKey, boolean>): number | null {
  const included: (number | null)[] = []
  for (const row of COST_ROWS) {
    if (row.required || flags[row.flag]) {
      const parsed = parseMoney(costs[row.key])
      included.push(parsed != null && !Number.isNaN(parsed) ? parsed : null)
    }
  }
  if (included.every((v) => v == null)) return null
  return included.reduce<number>((acc, v) => acc + (v ?? 0), 0)
}

function moneyToInput(value: number | null): string {
  return value != null ? String(value) : ""
}

function moneyOrDash(value: number | null): string {
  return formatMoney(value) || "—"
}

function textOrDash(value: string | null): string {
  return value && value.trim() ? value : "—"
}

// Validate a money input: blank → null (cleared), a number → that number,
// anything else → not ok (caller surfaces an error and blocks the save).
function readMoney(raw: string): { ok: true; value: number | null } | { ok: false } {
  const parsed = parseMoney(raw)
  if (parsed !== null && Number.isNaN(parsed)) return { ok: false }
  return { ok: true, value: parsed }
}

// ---------------------------------------------------------------------------
// Styles — THEME tokens only. Shell mirrors AuctionRowAlertModal; secondary
// button cites DeletePlateConfirm.tsx:135; caption uses THEME.typography.caption.
// ---------------------------------------------------------------------------

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  fontFamily: THEME.fonts.primary,
}

const containerStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.lg,
  // Fits a phone (94vw) and caps on desktop (820px) — no fixed min that would
  // overflow a narrow viewport.
  minWidth: 0,
  width: "min(820px, 94vw)",
  maxHeight: "88vh",
  overflowY: "auto",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
}

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
}

const headerRightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: THEME.spacing.sm,
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: "-0.5px",
  color: THEME.colors.neutral.light.textPrimary,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 2,
}

const closeButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  background: "none",
  border: "none",
  color: THEME.colors.neutral.light.textSecondary,
  cursor: "pointer",
  padding: 4,
}

const captionStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const selectStyle: React.CSSProperties = {
  fontFamily: THEME.fonts.primary,
  fontSize: 13,
  padding: "6px 8px",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  minWidth: 170,
  outline: "none",
}

const dispositionRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  background: THEME.colors.neutral.light.background,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  marginBottom: THEME.spacing.md,
}

function dispositionValueStyle(value: number | null): React.CSSProperties {
  return {
    fontSize: 20,
    fontWeight: 600,
    color:
      value != null && value < 0
        ? THEME.colors.semantic.danger
        : THEME.colors.neutral.light.textPrimary,
  }
}

const panelsRowStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
}

const panelStyle: React.CSSProperties = {
  flex: "1 1 200px",
  minWidth: 200,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
}

const costPanelStyle: React.CSSProperties = {
  // Third box in the panels row, alongside Details + Market Value.
  flex: "1 1 260px",
  minWidth: 240,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  display: "flex",
  flexDirection: "column",
  gap: 6,
}

const costHintStyle: React.CSSProperties = {
  textTransform: "none",
  fontWeight: 400,
  letterSpacing: 0,
}

const rrPanelStyle: React.CSSProperties = {
  marginTop: THEME.spacing.md,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
}

const rrNotesTextStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.5,
  color: THEME.colors.neutral.light.textPrimary,
  whiteSpace: "pre-wrap",
  maxHeight: 220,
  overflowY: "auto",
  background: THEME.colors.neutral.light.background,
  borderRadius: THEME.borderRadius.sm,
  padding: THEME.spacing.sm,
}

const rrEmptyStyle: React.CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: THEME.colors.neutral.light.textSecondary,
}

const costRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "18px 1fr 80px",
  alignItems: "center",
  gap: THEME.spacing.xs,
}

const statRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: THEME.spacing.md,
}

const statLabelStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
}

const statValueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  textAlign: "right",
}

const totalRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: THEME.spacing.md,
  fontSize: 14,
  color: THEME.colors.neutral.light.textPrimary,
  borderTop: `1px solid ${THEME.colors.silver}`,
  marginTop: THEME.spacing.xs,
  paddingTop: THEME.spacing.sm,
}

const actionPanelStyle: React.CSSProperties = {
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.md,
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const actionGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: THEME.spacing.md,
}

// Compact input used inside the Cost panel rows.
const inlineInputStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: THEME.fonts.primary,
  fontSize: 13,
  padding: "4px 8px",
  textAlign: "right",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  outline: "none",
  boxSizing: "border-box",
}

const fieldInputStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: THEME.fonts.primary,
  fontSize: 14,
  padding: "6px 8px",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  outline: "none",
  boxSizing: "border-box",
}

const textareaStyle: React.CSSProperties = {
  ...fieldInputStyle,
  resize: "vertical",
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: THEME.spacing.md,
  marginTop: THEME.spacing.md,
}

const errorTextStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.semantic.danger,
  marginRight: "auto",
}

// Secondary button — cite components/photoConfiguration/DeletePlateConfirm.tsx:135.
const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

// Brand primary button — cite components/audit/DownloadMenu.tsx:146.
function primaryButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: 14,
    fontWeight: 500,
    color: THEME.colors.white,
    backgroundColor: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}
